import dayjs from 'dayjs'

import { MemberFields } from '@tribeplatform/gql-client/graphql'
import {
  CustomFieldPrivacyOptions,
  CustomFieldSchema,
  Member,
} from '@tribeplatform/gql-client/types'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { useSlate } from '@tribeplatform/slate-kit/hooks'

import { FieldPrivacyBadges } from '../CustomField/FieldPrivacyBadges'
import { FieldView } from '../CustomField/FieldView'
import { getFieldSetting } from '../CustomField/utils'

export const MemberAbout = ({
  member,
  memberFields,
  highlightedField,
  preview = false,
}: {
  member: Member
  memberFields: MemberFields | CustomFieldSchema[]
  highlightedField: string
  preview?: boolean
}) => {
  const { context } = useSlate()
  const { network } = context
  const networkMemberFields = (
    memberFields ||
    network?.memberFields?.fields ||
    []
  ).filter(field => !field.archived)

  return (
    <Card>
      <Card.Header>
        <h3 className="text-lg leading-6 font-medium text-basicSurface-900">
          About
        </h3>
      </Card.Header>
      <Card.Content>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-1">
          {member?.tagline ? (
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-basicSurface-500">
                Tagline
              </dt>
              <dd className="mt-1 text-sm text-basicSurface-900">
                {member?.tagline}
              </dd>
            </div>
          ) : null}
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-basicSurface-500">
              Member since
            </dt>
            <dd className="mt-1 text-sm text-basicSurface-900">
              {dayjs(member.createdAt).toDate().toLocaleDateString()}
            </dd>
          </div>
          {false && member?.lastSeenAt ? (
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-basicSurface-500">
                Last seen
              </dt>
              <dd className="mt-1 text-sm text-basicSurface-900">
                {dayjs(member.lastSeenAt).fromNow()}
              </dd>
            </div>
          ) : null}
          {member?.email ? (
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-basicSurface-500 flex space-x-1 items-center">
                <span>Email</span>
                <FieldPrivacyBadges
                  field={{
                    readPrivacy: { allow: [CustomFieldPrivacyOptions.OWN] },
                  }}
                />
              </dt>
              <dd className="mt-1 text-sm text-basicSurface-900">
                <Link
                  href={`mailto:${member.email}`}
                  target="_blank"
                  external
                  variant="neutral"
                >
                  {member.email}
                </Link>
              </dd>
            </div>
          ) : null}
          {false && member?.score ? (
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-basicSurface-500">
                Score
              </dt>
              <dd className="mt-1 text-sm text-basicSurface-900">
                {member.score?.toLocaleString()}
              </dd>
            </div>
          ) : null}
          {networkMemberFields &&
            networkMemberFields
              ?.filter(
                field => getFieldSetting(field, 'category') !== 'socialLink',
              )
              .map(networkField => {
                return (
                  <FieldView
                    key={networkField.key}
                    field={networkField}
                    member={member}
                    highlighted={highlightedField === networkField.key}
                    showLabel
                    preview={preview}
                  />
                )
              })}
        </dl>
        <div className="flex flex-wrap mt-5">
          {networkMemberFields &&
            networkMemberFields
              ?.filter(
                field => getFieldSetting(field, 'category') === 'socialLink',
              )
              .map(networkField => {
                return (
                  <div
                    className="w-8 h-8 mr-2 mb-2 empty:hidden"
                    key={networkField.key}
                    data-id={networkField.key}
                  >
                    <FieldView
                      field={networkField}
                      member={member}
                      highlighted={highlightedField === networkField.key}
                      showLabel
                      preview={preview}
                    />
                  </div>
                )
              })}
        </div>
      </Card.Content>
    </Card>
  )
}
